import { ConnectedOverlayPositionChange } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';
import { AutocompleteRemoteBase } from './autocomplete-remote.base';
import { AutoCompleteSelectionItem } from './autocomplete-selection-item';
import { IAutoCompleteService } from './autocomplete-service.interface';
import {
  ASTRAL_DROPDOWN_OVERLAY_VIEWPORT_MARGIN,
  getAstralDropdownOverlayPositions,
  getAstralDropdownOverlayWidth,
} from '../astral-components/astral-dropdown-overlay.util';
import { getNextListboxOptionIndex } from '../astral-components/astral-listbox-keyboard.util';

/* ...re-exports to simplify imports elsewhere. */
export { AutoCompleteSelectionItem, IAutoCompleteService };

@Component({
  selector: 'app-autocomplete-remote',
  templateUrl: './autocomplete-remote.component.html',
  styleUrls: ['./autocomplete-remote.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AutocompleteRemoteComponent<TData = any, TOptions = any> extends AutocompleteRemoteBase<TData, TOptions> {
  private static nextId = 0;
  private readonly uid = `astral-remote-autocomplete-${++AutocompleteRemoteComponent.nextId}`;
  readonly overlayViewportMargin = ASTRAL_DROPDOWN_OVERLAY_VIEWPORT_MARGIN;
  private resizeObserver?: ResizeObserver;

  @Input() inputId: string;
  @Input() inputName: string;

  constructor(
    fcf: FormControlExFactory,
    private readonly cd: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {
    super(fcf, false);
  }

  @Output() itemSelectedNewId = new EventEmitter<AutoCompleteSelectionItem>();
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('controlShell') controlShell: ElementRef<HTMLDivElement>;
  @ViewChildren('optionElement') optionElements: QueryList<ElementRef<HTMLElement>>;

  isFocused = false;
  isPanelOpen = false;
  isPanelAbove = false;
  activeOptionIndex = -1;
  overlayWidth: number;

  @HostBinding('tabindex') tabindex = -1;

  get hasValue(): boolean {
    return !!this.selectionControl?.value || !!this.control?.value;
  }

  @HostBinding('style.grid-column')
  get hostGridColumn(): null {
    return null;
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const caption = this.control?.caption
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return caption || this.uid;
  }

  get panelId(): string {
    return `${this.resolvedInputId}-panel`;
  }

  get activeOptionId(): string | null {
    return this.activeOptionIndex >= 0 ? `${this.resolvedInputId}-option-${this.activeOptionIndex}` : null;
  }

  get showClearThis(): boolean {
    return !this.control?.isReadonly && (this.filterActions || this.hasValue);
  }

  get overlayPositions() {
    return getAstralDropdownOverlayPositions();
  }

  @HostListener('focus')
  focusHost(): void {
    this.input?.nativeElement.focus();
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    if (!this.isPanelOpen) {
      return;
    }

    requestAnimationFrame(() => this.syncOverlayWidth());
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(event: Event): void {
    if (!this.isPanelOpen) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.closePanel();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.subscription.add(
      this.pageLoaded.subscribe(page => {
        if (page <= 1) {
          this.activeOptionIndex = -1;
        }
      })
    );
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined' && this.controlShell?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => this.syncOverlayWidth());
      this.resizeObserver.observe(this.controlShell.nativeElement);
    }

    if (this.initialText) {
      this.cd.detectChanges();
    }
  }

  override ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    super.ngOnDestroy();
  }

  handleBlur(): void {
    queueMicrotask(() => {
      if (document.activeElement === this.input?.nativeElement) {
        return;
      }

      this.finalizeInteraction();
    });
  }

  handleFocus(): void {
    this.isFocused = true;
    this.openPanel();
    this.onfocus();
  }

  handleInput(): void {
    if (!this.control?.isReadonly) {
      this.activeOptionIndex = -1;
      this.openPanel();
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.openPanel();
      this.moveActiveOption(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (event.key === 'Enter' && this.isPanelOpen && this.activeOptionIndex >= 0) {
      event.preventDefault();
      this.selectItem(this.itemsSource[this.activeOptionIndex]);
      return;
    }

    if (event.key === 'Escape' && this.isPanelOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.closePanel();
    }
  }

  handleOptionMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  selectItem(item: AutoCompleteSelectionItem<TData>): void {
    this.setControlValue(item);
    this.closePanel();
  }

  handleOverlayOutsideClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && this.elementRef.nativeElement.contains(target)) {
      return;
    }

    this.finalizeInteraction();
  }

  handleOverlayPositionChange(event: ConnectedOverlayPositionChange): void {
    this.isPanelAbove = event.connectionPair.overlayY === 'bottom';
  }

  clearBtnClicked(): boolean {
    const result = super.clearBtnClicked();
    this.closePanel();
    return result;
  }

  public setControlValue(item: AutoCompleteSelectionItem<TData>) {
    super.setControlValue(item);
    this.itemSelectedNewId.emit(item); // (note: this can be null)
  }

  private closePanel(): void {
    this.isPanelOpen = false;
    this.isPanelAbove = false;
    this.activeOptionIndex = -1;
  }

  private finalizeInteraction(): void {
    this.isFocused = false;
    this.syncSelectionFromSearch();
    this.closePanel();
    this.onblur();
  }

  private openPanel(): void {
    if (this.control?.disabled || this.control?.isReadonly) {
      return;
    }

    this.isPanelOpen = true;
    this.syncOverlayWidth();
  }

  private moveActiveOption(direction: 1 | -1): void {
    const optionCount = this.itemsSource?.length ?? 0;
    if (!optionCount) return;

    this.activeOptionIndex = getNextListboxOptionIndex(this.activeOptionIndex, optionCount, direction);
    this.scrollActiveOptionIntoView();
  }

  private scrollActiveOptionIntoView(): void {
    queueMicrotask(() => this.optionElements?.get(this.activeOptionIndex)?.nativeElement.scrollIntoView({ block: 'nearest' }));
  }

  private syncOverlayWidth(): void {
    const shellWidth = this.controlShell?.nativeElement.getBoundingClientRect().width;
    const viewportWidth = this.elementRef.nativeElement.ownerDocument?.documentElement?.clientWidth;

    if (!shellWidth) {
      return;
    }

    if (!viewportWidth) {
      this.overlayWidth = shellWidth;
      return;
    }

    this.overlayWidth = getAstralDropdownOverlayWidth(shellWidth, viewportWidth);
  }

  private syncSelectionFromSearch(): void {
    if (this.selectionControl.value) {
      if (this.itemsSource?.length === 1) {
        this.setControlValue(this.itemsSource[0]);
        return;
      }

      if (this.selectedItem) {
        this.setControlValue(this.selectedItem);
        return;
      }
    }

    this.setControlValue(null);
  }
}
